import { api } from '@/api';
import {
    AdminListMembersResult,
    InviteMemberInput,
    InviteMemberResult,
    ReinviteMembersInput,
    ReinviteMembersResult,
    ToggleActiveStatusInput,
    ToggleActiveStatusResult,
    UpdateAdminStatusInput,
    UpdateAdminStatusResult,
} from '@vivport/backend/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Admin Query Keys Factory
 * Centralized query key factory for admin-related React Query hooks
 */
export const adminKeys = {
  all: ['admin'] as const,
  members: () => [...adminKeys.all, 'members'] as const,
};

/**
 * List Members Query Hook
 * @requires Admin
 */
export const useAdminListMembers = () => {
  return useQuery({
    queryKey: adminKeys.members(),
    queryFn: (): Promise<AdminListMembersResult> => api.admin.listMembers(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Invite Member Mutation Hook
 * @requires Admin
 */
export const useAdminInviteMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: InviteMemberInput): Promise<InviteMemberResult> =>
      api.admin.inviteMember(input),
    onSuccess: () => {
      // Invalidate members list to refetch with new member
      queryClient.invalidateQueries({ queryKey: adminKeys.members() });
    },
  });
};

/**
 * Reinvite Members Mutation Hook
 * @requires Admin
 */
export const useAdminReinviteMembers = () => {
  return useMutation({
    mutationFn: (input: ReinviteMembersInput): Promise<ReinviteMembersResult> =>
      api.admin.reinviteMembers(input),
  });
};

/**
 * Update Admin Status Mutation Hook
 * @requires Admin
 */
export const useAdminUpdateAdminStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateAdminStatusInput): Promise<UpdateAdminStatusResult> =>
      api.admin.updateAdminStatus(input),
    onSuccess: () => {
      // Invalidate members list to refetch with updated admin status
      queryClient.invalidateQueries({ queryKey: adminKeys.members() });
    },
  });
};

/**
 * Toggle Active Status Mutation Hook
 * @requires Admin
 */
export const useAdminToggleActiveStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ToggleActiveStatusInput): Promise<ToggleActiveStatusResult> =>
      api.admin.toggleActiveStatus(input),
    onSuccess: () => {
      // Invalidate members list to refetch with updated active status
      queryClient.invalidateQueries({ queryKey: adminKeys.members() });
    },
  });
};

